import React from "react";
import { connect } from "react-redux";
import { loadAvailableHours, loadConsultants } from "../../../redux/actions/AvailableHoursActions";
import { saveHourUtilizationRequest } from "../../../redux/actions/HourUtilizationRequestActions";
import { getBlackoutPeriodActive } from "../../../redux/actions/UtilityActions";
import { LOAD_AVAILABLE_HOURS_SUCCESS } from "../../../redux/actions/ActionTypes";
import Loading from "../../common/loading/Loading";
import ConsultantNameFilter from "../common/searchFilters/ConsultantNameFilter";
import DataGrid from "../../common/DataGrids/DataGrid";
import AdminButtons from "./AdminButtons";
import Page from "../../common/Page";
import BlackoutAlert from "../../common/alerts/BlackoutAlert";
import QuarterHoursDetailModal from "./QuarterHourDetailsModal";
import AvailableHoursHeader from "./AvailableHoursHeader";

class AvailableHoursPage extends React.Component {
  calculateTotals(data) {
    // Only sum actual records (not the "—" zero records with id = -1)
    const realRecords = data.filter(row => row.id !== -1);

    const dollarAmount = realRecords.reduce((sum, row) => {
      if (typeof row.requestableHours === 'number' && typeof row.rateValue === 'number') {
        return sum + (row.requestableHours * row.rateValue);
      }
      return sum;
    }, 0);

    const pendingHours = realRecords.reduce((sum, row) => sum + (typeof row.pendingHours === 'number' ? row.pendingHours : 0), 0);
    const pendingValue = realRecords.reduce((sum, row) => {
      if (typeof row.pendingHours === 'number' && typeof row.rateValue === 'number') {
        return sum + (row.pendingHours * row.rateValue);
      }
      return sum;
    }, 0);

    return {
      hoursRemaining: realRecords.reduce((sum, row) => sum + (typeof row.requestableHours === 'number' ? row.requestableHours : 0), 0),
      dollarAmount: dollarAmount,
      pendingHours: pendingHours,
      pendingValue: pendingValue
    };
  }

  render() {
    // Only show data if a specific consultant is selected (not "All")
    const shouldShowData = !this.props.profile.isAdmin ||
                          (this.props.profile.isAdmin && this.state.selectedName && this.state.selectedName !== "All");

    const displayData = shouldShowData
      ? (this.state.filtered
          ? this.state.filteredResults
          : this.filterAndFillQuarters(
              this.props.availableHours.map((i) => {
                const requestableHours = i.hours - i.pendingHours;
                const totalValue = typeof requestableHours === 'number' && typeof i.rateValue === 'number'
                  ? (requestableHours * i.rateValue).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
                  : '—';
                return {
                  ...i,
                  requestableHours: requestableHours,
                  earnedOn: this.formatQuarterDisplay(i.earnedOn),
                  fullName: `${i.consultantLastName}, ${i.consultantFirstName}`,
                  totalValue: typeof requestableHours === 'number' && typeof i.rateValue === 'number' ? `$${totalValue}` : '—'
                };
              })
            ))
      : [];

    const totals = shouldShowData ? this.calculateTotals(displayData) : { hoursRemaining: 0, dollarAmount: 0, pendingHours: 0, pendingValue: 0 };

    return (
      <Page
        pageTitle="Available Hours"
        alwaysShowHeader={true}
        ignoreStyleForHead={!(this.props.profile.isAdmin && !this.props.profile.ghost)}
        pageHeader={
          this.props.profile.isAdmin && !this.props.profile.ghost ? (
            <div style={{ width: '50%', maxWidth: '400px' }}>
              <ConsultantNameFilter
                onChange={this.filterResults}
                names={this.props.consultants.map((c) => c.fullName)}
                ref={(element) => (this.consultantNameFilter = element)}
              />
            </div>
          ) : null
        }
      >
        {this.props.blackedOut ? <BlackoutAlert /> : null}
        <Loading api={LOAD_AVAILABLE_HOURS_SUCCESS} />
        {shouldShowData ? (
          <>
            <AvailableHoursHeader
              totals={totals}
              blackedOut={this.props.blackedOut}
              consultantId={this.state.selectedConsultantId || this.props.profile.ghost}
              onRefresh={this.refreshData}
              showEmployeeLabel={this.props.profile.isAdmin && !this.props.profile.ghost && this.state.selectedName && this.state.selectedName !== "All"}
              employeeName={this.state.selectedName && this.state.selectedName.includes(", ") ? this.state.selectedName.split(", ").reverse().join(" ") : this.state.selectedName}
            />
            {totals.pendingHours > 0 && (
              <div style={{
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px',
                width: '100%'
              }}>
                <h5 style={{ marginTop: '0', marginBottom: '10px' }}>Pending Requests</h5>
                <div style={{ maxWidth: '700px' }}>
                  <DataGrid
                    adminHeaders={this.state.adminHeaders}
                    data={displayData
                      .filter(row => typeof row.pendingHours === 'number' && row.pendingHours > 0)
                      .map(row => ({
                        ...row,
                        pendingValue: `$${(row.pendingHours * row.rateValue).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                      }))}
                    disablePagination={true}
                    headers={this.state.pendingHeaders}
                    hideExport={true}
                    isAdmin={this.props.profile.isAdmin}
                    isGhosting={this.props.isGhosting}
                  />
                </div>
              </div>
            )}
            <div style={{
              border: '1px solid #e0e0e0',
              borderRadius: '12px',
              padding: '16px',
              width: '100%'
            }}>
              <h5 style={{ marginTop: '0', marginBottom: '10px' }}>Available Hours</h5>
              <DataGrid
                actionButtons={this.actionButtons}
                adminHeaders={this.state.adminHeaders}
                data={displayData}
                disablePagination={true}
                exportName="availibleRevShareHours"
                headers={this.state.headers}
                hideExport={true}
                isAdmin={this.props.profile.isAdmin}
                isGhosting={this.props.isGhosting}
              />
            </div>
          </>
        ) : (
          this.props.profile.isAdmin && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
              Please select a consultant to view their available hours.
            </div>
          )
        )}
      </Page>
    );
  }

  constructor(props) {
    super(props);
    this.actionButtons = this.actionButtons.bind(this);
    this.filterResults = this.filterResults.bind(this);
    this.sortByQuarter = this.sortByQuarter.bind(this);
    this.parseQuarter = this.parseQuarter.bind(this);
    this.formatQuarterDisplay = this.formatQuarterDisplay.bind(this);
    this.filterAndFillQuarters = this.filterAndFillQuarters.bind(this);
    this.calculateTotals = this.calculateTotals.bind(this);
    this.refreshData = this.refreshData.bind(this);

    this.state = {
      filteredResults: [],
      filtered: false,
      selectedName: "All",
      selectedConsultantId: null,
      shouldLoad: this.props.profile !== undefined,
      ghost: null,
      adminHeaders: [{ label: "Name", key: "consultantName" }],
      headers: [
        { label: "Quarter", key: "earnedOn" },
        {
          label: "Rate",
          key: "rate",
          headerStyle: { textAlign: "right" },
          style: { textAlign: "right" }
        },
        {
          label: "Hours Remaining",
          key: "requestableHours",
          headerStyle: { textAlign: "right", maxWidth: "90px", wordWrap: "break-word" },
          style: { textAlign: "right", maxWidth: "90px", wordWrap: "break-word" }
        },
        {
          label: "Total Value",
          key: "totalValue",
          headerStyle: { textAlign: "right", maxWidth: "100px", wordWrap: "break-word" },
          style: { textAlign: "right", maxWidth: "100px", wordWrap: "break-word" }
        },
      ],
      pendingHeaders: [
        { label: "Quarter", key: "earnedOn" },
        {
          label: "Rate",
          key: "rate",
          headerStyle: { textAlign: "right" },
          style: { textAlign: "right" }
        },
        {
          label: "Pending Hours",
          key: "pendingHours",
          headerStyle: { textAlign: "right", maxWidth: "90px", wordWrap: "break-word" },
          style: { textAlign: "right", maxWidth: "90px", wordWrap: "break-word" }
        },
        {
          label: "Pending Value",
          key: "pendingValue",
          headerStyle: { textAlign: "right", maxWidth: "100px", wordWrap: "break-word" },
          style: { textAlign: "right", maxWidth: "100px", wordWrap: "break-word" }
        },
      ],
    };
  }

  parseQuarter(earnedOn) {
    // Parse both "Q3 2023" and "2023 Q3" formats
    let match = earnedOn.match(/Q(\d)\s+(\d{4})/);
    if (match) {
      return { quarter: parseInt(match[1]), year: parseInt(match[2]) };
    }
    match = earnedOn.match(/(\d{4})\s+Q(\d)/);
    if (match) {
      return { quarter: parseInt(match[2]), year: parseInt(match[1]) };
    }
    return { quarter: 0, year: 0 };
  }

  formatQuarterDisplay(earnedOn) {
    // Convert "Q3 2023" to "2023 Q3"
    const match = earnedOn.match(/Q(\d)\s+(\d{4})/);
    if (match) {
      return `${match[2]} Q${match[1]}`;
    }
    return earnedOn;
  }

  sortByQuarter(data) {
    return data.sort((a, b) => {
      const aData = this.parseQuarter(a.earnedOn);
      const bData = this.parseQuarter(b.earnedOn);

      // Sort by year first, then by quarter
      if (aData.year !== bData.year) {
        return aData.year - bData.year;
      }
      return aData.quarter - bData.quarter;
    });
  }

  filterAndFillQuarters(data) {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 2;
    const endYear = currentYear - 1;

    // Note: API already filters to keep records from previous 2 years OR with hours > 0
    // This is just defensive filtering in case API changes
    const filtered = data;

    // Get unique consultants from filtered data
    const consultantIds = [...new Set(filtered.map((row) => row.consultantId))];

    // For each consultant, ensure all quarters in range exist
    const filledData = [...filtered];

    consultantIds.forEach((consultantId) => {
      const consultantRecords = filtered.filter((row) => row.consultantId === consultantId);

      // Use first record as template for missing quarters
      const template = consultantRecords[0];

      // Find the earliest year for this consultant (to fill gaps from old records to present)
      const years = consultantRecords.map((row) => this.parseQuarter(row.earnedOn).year);
      const earliestYear = Math.min(...years);

      // Use the earlier of: the consultant's earliest record OR the standard startYear
      const fillStartYear = Math.min(earliestYear, startYear);

      // Generate all quarters from the earliest record to the standard end year
      const allQuarters = [];
      for (let year = fillStartYear; year <= endYear; year++) {
        for (let q = 1; q <= 4; q++) {
          allQuarters.push({ quarter: q, year: year, earnedOn: `Q${q} ${year}` });
        }
      }

      allQuarters.forEach((q) => {
        const formattedQuarter = this.formatQuarterDisplay(q.earnedOn);
        const exists = consultantRecords.some((row) => row.earnedOn === formattedQuarter);
        if (!exists) {
          // Create a zero record for missing quarter
          filledData.push({
            id: -1, // Fake ID for client-side only records
            consultantId: template.consultantId,
            consultantName: template.consultantName,
            consultantFirstName: template.consultantFirstName,
            consultantLastName: template.consultantLastName,
            hours: "—",
            pendingHours: "—",
            requestableHours: "—",
            rate: "—",
            rateValue: template.rateValue,
            earnedOn: formattedQuarter,
            totalValue: "—",
          });
        }
      });
    });

    return this.sortByQuarter(filledData);
  }

  componentDidMount() {
    if (this.props.profile.isAdmin && !this.props.profile.ghost) {
      // For admins, just load the consultant list (lightweight)
      this.props.loadConsultants().catch((error) => alert(error));
    } else {
      // For non-admins, load their hours immediately
      this.props
        .loadAvailableHours(this.props.profile.isAdmin, this.props.profile.ghost)
        .then(
          this.setState({
            totalBalance:
              this.props.availableHours.length > 0
                ? this.props.availableHours.map((i) => i.hours * i.rateValue).reduce((total, i) => total + i)
                : 0.0,
          })
        )
        .catch((error) => alert(error));
    }
    this.setState({
      ghost: this.props.profile.ghost,
    });
    this.props.getBlackoutPeriodActive();
  }

  componentDidUpdate(prevProps) {
    // Load consultants when admin profile becomes available (wasn't loaded at mount)
    if (this.props.profile.isAdmin && !this.props.profile.ghost &&
        this.props.consultants.length === 0 &&
        (!prevProps.profile.isAdmin || prevProps.profile.ghost)) {
      this.props.loadConsultants().catch((error) => alert(error));
    }

    if (this.props.profile.ghost !== this.state.ghost) {
      if (this.props.profile.ghost) {
        // When ghosting starts, load that user's hours
        this.props
          .loadAvailableHours(this.props.profile.isAdmin, this.props.profile.ghost)
          .catch((error) => alert(error));
      } else if (this.props.profile.isAdmin) {
        // When ghosting ends for admin, reload consultant list and clear data
        this.props.loadConsultants().catch((error) => alert(error));
        this.setState({ selectedName: "All", filtered: false });
      }
      this.setState({ ghost: this.props.profile.ghost });
    }
  }

  actionButtons(row) {
    // Don't show action buttons for client-side zero records (id = -1)
    const isZeroRecord = row.id === -1;

    if (isZeroRecord) {
      return <span style={{ fontStyle: "italic", color: "#6c757d" }}>No RevShare earned for this quarter</span>;
    }

    return (
      <>
        <QuarterHoursDetailModal availableHoursId={row.id} quarter={row.earnedOn} key={row.id} />
        {!this.props.blackedOut && this.props.profile.isAdmin && !this.props.profile.ghost && (
          <AdminButtons AvailableHoursRecord={row} />
        )}
      </>
    );
  }

  filterResults() {
    const selectedName = this.consultantNameFilter.value;

    if (selectedName === "All") {
      // Clear data when "All" is selected
      this.setState({
        filteredResults: [],
        filtered: false,
        selectedName: "All",
        selectedConsultantId: null
      });
      return;
    }

    // Find the consultant ID for the selected name
    const consultant = this.props.consultants.find((c) => c.fullName === selectedName);

    if (consultant) {
      // Load hours for the selected consultant
      this.props
        .loadAvailableHours(true, consultant.consultantId)
        .then(() => {
          // After loading, process and display the data
          const results = this.filterAndFillQuarters(
            this.props.availableHours.map((i) => {
              const requestableHours = i.hours - i.pendingHours;
              const totalValue = typeof requestableHours === 'number' && typeof i.rateValue === 'number'
                ? (requestableHours * i.rateValue).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
                : '—';
              return {
                ...i,
                requestableHours: requestableHours,
                earnedOn: this.formatQuarterDisplay(i.earnedOn),
                fullName: `${i.consultantLastName}, ${i.consultantFirstName}`,
                totalValue: typeof requestableHours === 'number' && typeof i.rateValue === 'number' ? `$${totalValue}` : '—'
              };
            })
          );
          this.setState({
            filteredResults: results,
            filtered: true,
            selectedName: selectedName,
            selectedConsultantId: consultant.consultantId
          });
        })
        .catch((error) => alert(error));
    }
  }

  refreshData() {
    const consultantId = this.state.selectedConsultantId || this.props.profile.ghost;
    this.props
      .loadAvailableHours(this.props.profile.isAdmin, consultantId)
      .then(() => {
        // Re-process the data if we're in filtered mode (admin viewing specific consultant)
        if (this.state.filtered) {
          const results = this.filterAndFillQuarters(
            this.props.availableHours.map((i) => {
              const requestableHours = i.hours - i.pendingHours;
              const totalValue = typeof requestableHours === 'number' && typeof i.rateValue === 'number'
                ? (requestableHours * i.rateValue).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})
                : '—';
              return {
                ...i,
                requestableHours: requestableHours,
                earnedOn: this.formatQuarterDisplay(i.earnedOn),
                fullName: `${i.consultantLastName}, ${i.consultantFirstName}`,
                totalValue: typeof requestableHours === 'number' && typeof i.rateValue === 'number' ? `$${totalValue}` : '—'
              };
            })
          );
          this.setState({ filteredResults: results });
        }
      })
      .catch((error) => alert(error));
  }
}

function mapStateToProps(state, ownProps) {
  return {
    availableHours: state.availableHours,
    blackedOut: state.blackoutPeriod,
    consultants: state.consultants,
    profile: state.profile,
  };
}

const mapDispatchToProps = {
  getBlackoutPeriodActive,
  loadAvailableHours,
  loadConsultants,
  saveHourUtilizationRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(AvailableHoursPage);
